"use client";

import Image from "next/image";
// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, which
// costs a middleware redirect round-trip on every click.
import { Link } from "@/i18n/routing";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { EJTECH_COLORS, EJTECH_FONT, EJTECH_TYPE } from "@/lib/theme/ej-tech";
import { EJTECH_FOOTER_COLUMNS, SOCIAL_ICONS } from "@/fixtures/ej-tech/fixtures";

export function Footer() {
  // The columns are EJ Tech's own now. They used to be the MAIN site's
  // 新聞 / 專欄 / 信報品牌 / 更多 — 即時, 投資, 財經, 地產 and the rest, none of
  // which is this product's subject. QA: "update the foot items to related to
  // ej TECH".
  const columns = EJTECH_FOOTER_COLUMNS;

  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  /*
    A real check, not just `required`.

    The browser's own `type="email"` validation accepts "a@b" and blocks submit
    with a native bubble that carries no styling and disappears on the next
    keystroke. QA: "subscribe newsletter button: should validate the email
    before allow submit". The button is disabled until the address is
    plausible, and the reason is shown inline once the field has been touched.
  */
  const value = email.trim();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

  const onSubscribe = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setTouched(true);
      return;
    }
    setEmail("");
    setTouched(false);
    // TODO(api): POST to the list once a subscription endpoint exists.
    toast.success(`已訂閱：${value}`);
  };

  return (
    <footer
      className="w-full"
      style={{
        backgroundColor: EJTECH_COLORS.surfaceDark,
        fontFamily: EJTECH_FONT.body,
        color: EJTECH_COLORS.textOnDark,
      }}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-14 px-4 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-20">
        {/* Top: brand + statement */}
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
          <div className="flex max-w-[420px] flex-col gap-5">
            {/*
              The white-on-transparent mark, on the dark footer.

              The masthead logo is drawn for a LIGHT background — its "TECH"
              wordmark is near-black, so on this surface it disappeared and only
              the coloured glyph read. That is QA finding
              `6a69c020241244547333676a#2` (logo legibility), which had been
              parked as "an asset problem"; the asset now exists.

              The tinted plate the logo used to sit on is gone with it — it was
              there to lift a dark wordmark off a dark ground.
            */}
            {/* `h-10 w-auto` alone let the 244×103 source paint at whatever
                width the flex row gave it, so the mark came out stretched. The
                intrinsic ratio is declared and `object-contain` is explicit —
                QA: "the logo is being stretched, fix it". */}
            <Image
              src="/images/ej-tech/ej-tech-logo-white.png"
              alt="EJ Tech"
              width={244}
              height={103}
              // `self-start` is the load-bearing part: this sits in a flex
              // COLUMN, whose default `align-items: stretch` blew the mark out
              // to the container's full width at a fixed 40px height — a 244×103
              // logo rendered at roughly 420×40. `w-auto` cannot resist stretch.
              className="h-10 w-auto shrink-0 self-start object-contain"
              style={{ aspectRatio: "244 / 103" }}
              unoptimized
            />
            <p
              style={{
                ...EJTECH_TYPE.body,
                color: EJTECH_COLORS.textOnDarkMuted,
              }}
            >
              香港創新科技新聞 — 涵蓋人工智能、初創企業與商業策略。
            </p>
            <div className="flex items-center gap-2 pt-2">
              {SOCIAL_ICONS.map((s) => (
                <a
                  key={s.alt}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.alt}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200 hover:scale-105"
                  style={{
                    border: `1px solid ${EJTECH_COLORS.hairlineOnDark}`,
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      EJTECH_COLORS.primary;
                    e.currentTarget.style.borderColor = EJTECH_COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor =
                      EJTECH_COLORS.hairlineOnDark;
                  }}
                >
                  <Image
                    src={s.src}
                    alt={s.alt}
                    width={16}
                    height={16}
                    className="brightness-0 invert"
                    unoptimized
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Columns */}
          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4 lg:w-auto lg:gap-x-14">
            {columns.map((col) => (
              <FooterColumn key={col.title} heading={col.title}>
                {col.items.map((item) => (
                  <FooterLink key={item.label} href={item.href}>
                    {item.label}
                  </FooterLink>
                ))}
              </FooterColumn>
            ))}
          </div>
        </div>

        {/* Newsletter strip */}
        <div
          className="flex flex-col gap-4 rounded-3xl p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between"
          style={{
            backgroundColor: "rgba(16,168,90,0.08)",
            border: `1px solid ${EJTECH_COLORS.hairlineOnDark}`,
          }}
        >
          <div className="flex flex-col gap-1">
            <span
              style={{
                ...EJTECH_TYPE.eyebrow,
                color: EJTECH_COLORS.primary,
              }}
            >
              EJ TECH
            </span>
            <h3
              className="m-0"
              style={{
                ...EJTECH_TYPE.h3,
                color: EJTECH_COLORS.textOnDark,
              }}
            >
              接收最新創科資訊
            </h3>
          </div>
          {/* Submitting did nothing at all — the form had no handler, so the
              page just reloaded. QA: "subscribe newsletter button: submit email
              then show toast". */}
          <form
            onSubmit={onSubscribe}
            className="flex w-full max-w-[420px] items-center gap-2 lg:w-auto"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="你的電郵地址"
              aria-label="你的電郵地址"
              aria-invalid={touched && value.length > 0 && !valid}
              className="w-full rounded-full px-4 py-3 outline-none"
              suppressHydrationWarning
              style={{
                ...EJTECH_TYPE.bodySm,
                color: EJTECH_COLORS.textOnDark,
                backgroundColor: "rgba(255,255,255,0.06)",
                border: `1px solid ${EJTECH_COLORS.hairlineOnDark}`,
              }}
            />
            <button
              type="submit"
              disabled={!valid}
              className="inline-flex shrink-0 items-center rounded-full px-5 py-3 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              style={{
                ...EJTECH_TYPE.navLink,
                fontSize: 13,
                fontWeight: 700,
                color: EJTECH_COLORS.textOnDark,
                backgroundColor: EJTECH_COLORS.primary,
              }}
            >
              成為會員
            </button>
          </form>
          {touched && value.length > 0 && !valid && (
            <p
              role="alert"
              className="m-0 pt-2"
              style={{ ...EJTECH_TYPE.meta, color: EJTECH_COLORS.danger }}
            >
              請輸入有效的電郵地址。
            </p>
          )}
        </div>

        {/* Bottom strip */}
        <div
          className="flex flex-col gap-4 pt-8 sm:flex-row sm:items-center sm:justify-between"
          style={{ borderTop: `1px solid ${EJTECH_COLORS.hairlineOnDark}` }}
        >
          <p
            className="m-0"
            style={{
              ...EJTECH_TYPE.meta,
              color: EJTECH_COLORS.textOnDarkMuted,
            }}
          >
            © 2026 信報財經新聞．EJ Tech 版權所有
          </p>
          <div className="flex items-center gap-5">
            {[
              { label: "服務協議", href: "/legal/membership" },
              { label: "私隱政策", href: "/legal/privacy" },
              { label: "免責聲明", href: "/legal/disclaimer" },
            ].map(({ label, href }) => (
              <Link
                key={label}
                href={href}
                className="transition-opacity hover:opacity-80"
                style={{
                  ...EJTECH_TYPE.meta,
                  color: EJTECH_COLORS.textOnDarkMuted,
                }}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <h4
        className="m-0"
        style={{
          ...EJTECH_TYPE.eyebrow,
          color: EJTECH_COLORS.primary,
        }}
      >
        {heading}
      </h4>
      <ul className="flex flex-col gap-2.5">{children}</ul>
    </div>
  );
}

/** A label with no page renders as plain text, never as a dead `href="#"`. */
function FooterLink({
  href,
  children,
}: {
  href?: string;
  children: React.ReactNode;
}) {
  if (!href) {
    return (
      <li>
        <span
          className="block"
          style={{
            ...EJTECH_TYPE.bodySm,
            color: EJTECH_COLORS.textOnDarkMuted,
          }}
        >
          {children}
        </span>
      </li>
    );
  }
  return (
    <li>
      <Link
        href={href}
        className="block transition-colors"
        style={{
          ...EJTECH_TYPE.bodySm,
          color: EJTECH_COLORS.textOnDarkMuted,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.color =
            EJTECH_COLORS.accent;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.color =
            EJTECH_COLORS.textOnDarkMuted;
        }}
      >
        {children}
      </Link>
    </li>
  );
}
